'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * LoginIdentifierInput
 *
 * Unified input for the login "Email / Phone" field.
 *
 * - If the user types an email (contains @), renders a standard text input
 *   with inline email-format validation (red message when incomplete/invalid).
 * - If the user types 3+ digits that look like a South African mobile number
 *   (starts with 0 or 27 followed by 6x/7x/8x), switches to OTP-style blocks:
 *
 *       [+27] [XX] [XXX] [XXXX]
 *
 *   The +27 is fixed/implied. The user fills 9 national digits across 3
 *   blocks. Auto-advance, backspace navigation, paste support, and active-
 *   block color highlighting are copied from the MFA page.
 * - Falls back to a plain text input for non-SA phone numbers or short digit
 *   strings that don't match the SA mobile pattern.
 */

// ── Email validation ──────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isCompleteEmail(value: string): boolean {
  return EMAIL_REGEX.test(value)
}

// ── SA phone detection ────────────────────────────────────────────
// SA mobile numbers start with 0 (national) or 27 (international without +),
// followed by 6x/7x/8x, then 8 more digits.
// Common prefixes: 060-063, 064-068, 071-079, 081-084, 086-089
// Also matches +276x / 276x for already-normalised values (e.g. remembered users).
const SA_MOBILE_PREFIX = /^(0|27)(6[0-9]|7[0-9]|8[0-9])/

/**
 * Returns true once the user has typed at least 3 chars that look like the
 * start of a SA mobile number (e.g. "066", "082", "071", "+2766", "2766").
 */
function isSaPhonePrefix(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  if (digits.length < 3) return false
  // Must start with 0 or 27, followed by 6x/7x/8x
  return SA_MOBILE_PREFIX.test(digits)
}

// ── Block config ──────────────────────────────────────────────────
// [+27] is a fixed, read-only prefix block. SA mobile numbers have 9
// national digits after the leading 0 / +27 (e.g. 0664546654 → 664546654).
// The user fills those 9 digits across 3 editable blocks: 2-3-4.
// Full display: [+27] [XX] [XXX] [XXXX]
const BLOCK_SIZES = [2, 3, 4]
const TOTAL_INPUT_DIGITS = BLOCK_SIZES.reduce((a, b) => a + b, 0) // 9

interface LoginIdentifierInputProps {
  value: string
  onChange: (value: string) => void
  onEnter?: () => void
  disabled?: boolean
  placeholder?: string
  label?: string
  autoComplete?: string
  id?: string
  name?: string
}

export function LoginIdentifierInput({
  value,
  onChange,
  onEnter,
  disabled = false,
  placeholder = 'Email address or phone number',
  label = 'Email / Phone',
  autoComplete = 'username',
  id = 'email-address',
  name = 'email',
}: LoginIdentifierInputProps) {
  // Determine mode from the current value
  const isEmailMode = value.includes('@')
  const digitsOnly = useMemo(() => value.replace(/\D/g, ''), [value])
  const isPhoneBlockMode = !isEmailMode && isSaPhonePrefix(value) && digitsOnly.length >= 3

  // ── Email mode ──────────────────────────────────────────────────
  const emailError = useMemo(() => {
    if (!value || isPhoneBlockMode) return null
    if (isEmailMode) {
      if (value.length < 5) return 'Email address not complete'
      if (!isCompleteEmail(value)) return 'Please enter a valid email address'
      return null
    }
    // Not email and not phone-block — could be a partial phone or other input
    // Only show email error if they've typed something that looks like an attempt at email
    if (value.length > 0 && !/\d/.test(value) && !value.includes('@')) {
      return 'Please enter a valid email address or phone number'
    }
    return null
  }, [value, isEmailMode, isPhoneBlockMode])

  // ── Phone block mode ────────────────────────────────────────────
  const blockRefs = useRef<(HTMLInputElement | null)[]>([])
  const [activeBlock, setActiveBlock] = useState(-1)
  const wasPhoneBlockMode = useRef(false)

  // Derive block values from the canonical value
  const blockValues = useMemo(() => {
    if (!isPhoneBlockMode) return []
    // Extract the 9 national digits, handling both national (0xx) and
    // international (27xx / +27xx) formats so remembered users with
    // normalised phones like "+27664546654" also render correctly.
    let nationalDigits: string
    if (digitsOnly.startsWith('0')) {
      nationalDigits = digitsOnly.slice(1) // strip leading 0
    } else if (digitsOnly.startsWith('27')) {
      nationalDigits = digitsOnly.slice(2) // strip country code 27
    } else {
      nationalDigits = digitsOnly
    }
    const padded = nationalDigits.padEnd(TOTAL_INPUT_DIGITS, '')
    const values: string[] = []
    let offset = 0
    for (const size of BLOCK_SIZES) {
      values.push(padded.slice(offset, offset + size))
      offset += size
    }
    return values
  }, [digitsOnly, isPhoneBlockMode])

  // When transitioning from text-input mode to phone-block mode (e.g. the
  // user just typed "066"), focus the first block that still has room for
  // input so they can continue typing without clicking.
  useEffect(() => {
    if (!isPhoneBlockMode || wasPhoneBlockMode.current) {
      wasPhoneBlockMode.current = isPhoneBlockMode
      return
    }
    // Mode just switched to block mode — find the first block that isn't full
    wasPhoneBlockMode.current = true
    const focusIdx = blockValues.findIndex((bv, i) => bv.length < BLOCK_SIZES[i])
    const targetIdx = focusIdx === -1 ? BLOCK_SIZES.length - 1 : focusIdx
    // Defer focus to next tick so the block inputs have mounted
    const timer = window.setTimeout(() => {
      const el = blockRefs.current[targetIdx]
      if (el) {
        el.focus()
        // Place cursor at end of any existing digits in the block
        const len = el.value.length
        el.setSelectionRange(len, len)
        setActiveBlock(targetIdx)
      }
    }, 0)
    return () => window.clearTimeout(timer)
  }, [isPhoneBlockMode, blockValues])

  // Update the canonical value from block values
  const updateFromBlocks = useCallback(
    (newBlockValues: string[]) => {
      const joined = newBlockValues.join('')
      // Reconstruct: 0 + 9 digits → normalizePhoneNumber will turn into +27XXXXXXXXX
      const truncated = joined.slice(0, TOTAL_INPUT_DIGITS)
      // Always prefix with 0 so normalizePhoneNumber treats it as SA national
      onChange(`0${truncated}`)
    },
    [onChange],
  )

  const handleBlockChange = useCallback(
    (blockIdx: number, rawValue: string) => {
      const size = BLOCK_SIZES[blockIdx]
      const current = blockValues[blockIdx] || ''
      const newBlocks = [...blockValues]

      // Strip non-digits
      const digits = rawValue.replace(/\D/g, '')

      // Deletion: input cleared or got shorter
      if (digits === '' || digits.length < current.length) {
        newBlocks[blockIdx] = digits.slice(0, size)
        updateFromBlocks(newBlocks)
        if (newBlocks[blockIdx].length === 0 && blockIdx > 0) {
          window.setTimeout(() => {
            blockRefs.current[blockIdx - 1]?.focus()
            setActiveBlock(blockIdx - 1)
          }, 0)
        }
        return
      }

      // Single keypress: rawValue is current + exactly one new digit
      // (browser appends the typed char to the existing input value)
      if (digits.length === current.length + 1 && digits.startsWith(current)) {
        const newDigit = digits[current.length]
        if (current.length < size) {
          // Append to current block
          newBlocks[blockIdx] = current + newDigit
        } else {
          // Block full — replace last digit
          newBlocks[blockIdx] = current.slice(0, -1) + newDigit
        }
        updateFromBlocks(newBlocks)

        // Auto-advance to next block when full
        if (newBlocks[blockIdx].length >= size && blockIdx < BLOCK_SIZES.length - 1) {
          window.setTimeout(() => {
            blockRefs.current[blockIdx + 1]?.focus()
            setActiveBlock(blockIdx + 1)
          }, 0)
        }
        return
      }

      // Paste or multi-digit insertion: distribute digits across blocks
      // starting from the current block position
      const allDigits = digits.slice(0, TOTAL_INPUT_DIGITS)
      let cursor = blockIdx
      let digitIdx = 0

      // Keep digits from earlier blocks intact, only redistribute from cursor
      while (digitIdx < allDigits.length && cursor < BLOCK_SIZES.length) {
        const blockSize = BLOCK_SIZES[cursor]
        newBlocks[cursor] = allDigits.slice(digitIdx, digitIdx + blockSize)
        digitIdx += blockSize
        if (newBlocks[cursor].length >= blockSize) {
          cursor++
        }
      }

      updateFromBlocks(newBlocks)

      // Focus the block at the cursor position (or last block)
      const focusIdx = Math.min(cursor, BLOCK_SIZES.length - 1)
      window.setTimeout(() => {
        const el = blockRefs.current[focusIdx]
        if (el) {
          el.focus()
          const len = el.value.length
          el.setSelectionRange(len, len)
        }
        setActiveBlock(focusIdx)
      }, 0)
    },
    [blockValues, updateFromBlocks],
  )

  const handleBlockKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, blockIdx: number) => {
      if (e.key === 'Backspace') {
        const current = blockValues[blockIdx] || ''
        if (current.length === 0 && blockIdx > 0) {
          e.preventDefault()
          const prev = blockValues[blockIdx - 1] || ''
          const newBlocks = [...blockValues]
          newBlocks[blockIdx - 1] = prev.slice(0, -1)
          updateFromBlocks(newBlocks)
          window.setTimeout(() => {
            blockRefs.current[blockIdx - 1]?.focus()
            setActiveBlock(blockIdx - 1)
          }, 0)
        }
      } else if (e.key === 'ArrowLeft' && blockIdx > 0) {
        e.preventDefault()
        blockRefs.current[blockIdx - 1]?.focus()
        setActiveBlock(blockIdx - 1)
      } else if (e.key === 'ArrowRight' && blockIdx < BLOCK_SIZES.length - 1) {
        e.preventDefault()
        blockRefs.current[blockIdx + 1]?.focus()
        setActiveBlock(blockIdx + 1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        onEnter?.()
      }
    },
    [blockValues, updateFromBlocks, onEnter],
  )

  const handleBlockPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, blockIdx: number) => {
      e.preventDefault()
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, TOTAL_INPUT_DIGITS)
      if (!pasted) return
      const newBlocks = [...blockValues]
      // Distribute pasted digits across blocks starting from blockIdx
      let digitIdx = 0
      let cursor = blockIdx
      while (digitIdx < pasted.length && cursor < BLOCK_SIZES.length) {
        const size = BLOCK_SIZES[cursor]
        newBlocks[cursor] = pasted.slice(digitIdx, digitIdx + size)
        digitIdx += size
        cursor++
      }
      updateFromBlocks(newBlocks)
      const focusIdx = Math.min(cursor, BLOCK_SIZES.length - 1)
      window.setTimeout(() => {
        const el = blockRefs.current[focusIdx]
        if (el) {
          el.focus()
          const len = el.value.length
          el.setSelectionRange(len, len)
        }
        setActiveBlock(focusIdx)
      }, 0)
    },
    [blockValues, updateFromBlocks],
  )

  // Validate phone completeness (derived, not stateful)
  const phoneError = useMemo(() => {
    if (!isPhoneBlockMode) return null
    // Calculate national digit count by stripping the prefix (0 or 27)
    let nationalDigits: string
    if (digitsOnly.startsWith('0')) {
      nationalDigits = digitsOnly.slice(1)
    } else if (digitsOnly.startsWith('27')) {
      nationalDigits = digitsOnly.slice(2)
    } else {
      nationalDigits = digitsOnly
    }
    if (nationalDigits.length < TOTAL_INPUT_DIGITS) return 'Phone number not complete'
    if (nationalDigits.length > TOTAL_INPUT_DIGITS) return 'Phone number is too long'
    return null
  }, [digitsOnly, isPhoneBlockMode])

  // ── Render ──────────────────────────────────────────────────────

  if (isPhoneBlockMode) {
    return (
      <div>
        <label htmlFor={`${id}-block-0`} className="pm-label text-center block mb-3">
          {label}
        </label>
        <div className="flex justify-center items-center space-x-2">
          {/* South African flag — borderless, just the flag glyph */}
          <span
            className="fi fi-za text-3xl leading-none"
            aria-label="South Africa"
            role="img"
          />
          {/* Fixed +27 prefix */}
          <div className="flex h-12 items-center justify-center rounded-md border-2 border-teal-400 bg-teal-50 px-2 text-lg font-bold text-teal-700 dark:border-teal-600 dark:bg-teal-900/20 dark:text-teal-300">
            +27
          </div>
          {/* Editable blocks */}
          {BLOCK_SIZES.map((size, blockIdx) => {
            const widthClass =
              size === 2
                ? 'w-12'
                : size === 3
                  ? 'w-16'
                  : 'w-20'
            const isActive = activeBlock === blockIdx
            const hasError = phoneError !== null
            return (
              <input
                key={blockIdx}
                id={`${id}-block-${blockIdx}`}
                ref={(el) => {
                  blockRefs.current[blockIdx] = el
                }}
                type="text"
                inputMode="numeric"
                maxLength={size}
                value={blockValues[blockIdx] || ''}
                onChange={(e) => handleBlockChange(blockIdx, e.target.value)}
                onKeyDown={(e) => handleBlockKeyDown(e, blockIdx)}
                onPaste={(e) => handleBlockPaste(e, blockIdx)}
                onFocus={() => setActiveBlock(blockIdx)}
                onBlur={() => setActiveBlock(-1)}
                autoComplete="off"
                pattern="[0-9]*"
                disabled={disabled}
                className={`${widthClass} h-12 text-center text-xl font-semibold border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 dark:bg-gray-700 dark:text-white transition-colors ${
                  hasError
                    ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                    : isActive
                      ? 'border-teal-500 bg-teal-50 dark:border-teal-500 dark:bg-teal-900/20'
                      : 'border-gray-300 dark:border-gray-600'
                }`}
              />
            )
          })}
        </div>
        {phoneError && (
          <div className="mt-1 text-center text-sm text-red-600 dark:text-red-400">{phoneError}</div>
        )}
        {!phoneError && digitsOnly.length >= 4 && (
          <div className="mt-1 text-center text-sm text-green-600 dark:text-green-400">
            ✓ South African number detected
          </div>
        )}
      </div>
    )
  }

  // Standard text input (email or non-SA phone)
  return (
    <div>
      <label htmlFor={id} className="pm-label">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type="text"
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            onEnter?.()
          }
        }}
        className="pm-input"
        placeholder={placeholder}
        disabled={disabled}
      />
      {emailError && (
        <div className="mt-1 text-center text-sm text-red-600 dark:text-red-400">{emailError}</div>
      )}
      {isEmailMode && !emailError && value && isCompleteEmail(value) && (
        <div className="mt-1 text-center text-sm text-green-600 dark:text-green-400">
          ✓ Valid email address
        </div>
      )}
    </div>
  )
}
